/**
 * VolunteerHome — /volunteer
 *
 * The main landing page for the Volunteer module. Structure:
 *   1. Hero band — "Together We Build Stronger Communities"
 *   2. Social Capital benefits (9 cards)
 *   3. Volunteer Requests strip — recent /api/opportunities
 *   4. Charities & Organizations tabbed grid (by cause category, with
 *      a country filter) — /api/organizations
 *   5. Community Impact Dashboard — live counts from /api/impact/global
 *   6. CTAs (Sign up · Publish an opportunity)
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import {
  Heart, Users, Globe2, GraduationCap, Briefcase, Star, Award,
  Sparkles, Smile, ArrowRight, Calendar, MapPin, Clock, Loader2,
  Building2, HandHelping, TreePine, Shield,
} from "lucide-react";
import COUNTRIES from "@/lib/countries";
import CountUp from "@/components/CountUp";
import CardIcon from "@/components/CardIcon";
import BenefitQuiz from "@/components/BenefitQuiz";

/* ─── Static config ─────────────────────────────────────────────── */
const BENEFITS = [
  { icon: Heart,        color: "#e11d48", emoji: "❤️", slug: "help-people",           title: "Help People",           blurb: "Serve neighbours, families and communities that need your time." },
  { icon: Users,        color: "#7c3aed", emoji: "🤝", slug: "build-relationships",   title: "Build Relationships",   blurb: "Meet mentors, teammates and lifelong friends." },
  { icon: Globe2,       color: "#059669", emoji: "🌱", slug: "strengthen-communities",title: "Strengthen Communities",blurb: "Every hour of service makes your neighborhood safer and kinder." },
  { icon: GraduationCap,color: "#0ea5e9", emoji: "🎓", slug: "learn-new-skills",      title: "Learn New Skills",      blurb: "Gain training, certifications and hands-on experience." },
  { icon: Briefcase,    color: "#b45309", emoji: "💼", slug: "gain-experience",       title: "Gain Experience",       blurb: "Real-world work that strengthens your résumé and college apps." },
  { icon: Star,         color: "#d97706", emoji: "⭐", slug: "earn-recognition",      title: "Earn Recognition",      blurb: "Digital badges, community awards and verified hours." },
  { icon: Award,        color: "#0d9488", emoji: "🏆", slug: "volunteer-awards",      title: "Volunteer Awards",      blurb: "Monthly Community Hero + annual recognition programs." },
  { icon: Sparkles,     color: "#7c3aed", emoji: "✨", slug: "personal-growth",       title: "Personal Growth",       blurb: "Discover purpose, resilience and confidence." },
  { icon: Smile,        color: "#16a34a", emoji: "😊", slug: "improve-wellbeing",     title: "Improve Wellbeing",     blurb: "Volunteering is scientifically linked to better mental health." },
];

// Vertical / cause categories used to segment the org directory. Keys
// must match values used in /api/organizations?category=… when they
// exist in the DB — we default to a text-search fallback if not.
const CAUSE_TABS = [
  { key: "food",         label: "Food & Nutrition",     icon: "🍽️",  causeMatch: ["food-security", "hunger", "food-bank", "nutrition"] },
  { key: "environment",  label: "Environment",          icon: "🌿",  causeMatch: ["environment", "climate", "conservation", "tree-planting", "ocean"] },
  { key: "education",    label: "Education & Youth",    icon: "🎓",  causeMatch: ["education", "youth", "children", "literacy"] },
  { key: "health",       label: "Health & Wellness",    icon: "❤️",  causeMatch: ["health", "medical", "mental-health", "disease"] },
  { key: "poverty",      label: "Poverty & Housing",    icon: "🏠",  causeMatch: ["poverty", "housing", "homelessness", "refugees"] },
  { key: "animals",      label: "Animal Welfare",       icon: "🐾",  causeMatch: ["animal-welfare", "wildlife", "conservation"] },
  { key: "disaster",     label: "Disaster Relief",      icon: "🚑",  causeMatch: ["disaster-relief", "emergency", "humanitarian"] },
  { key: "senior",       label: "Senior Care",          icon: "👵",  causeMatch: ["senior", "elder-care", "aging"] },
  { key: "arts",         label: "Arts & Culture",       icon: "🎨",  causeMatch: ["arts", "culture", "heritage", "music"] },
  { key: "rights",       label: "Human Rights",         icon: "⚖️",  causeMatch: ["human-rights", "equality", "advocacy"] },
];

/* ─── Component ─────────────────────────────────────────────────── */
export default function VolunteerHome() {
  const [impact, setImpact]        = useState(null);
  const [opps,   setOpps]          = useState([]);
  const [orgs,   setOrgs]          = useState([]);
  const [orgLoad,setOrgLoad]       = useState(true);
  const [country,setCountry]       = useState("");
  const [cause,  setCause]         = useState(CAUSE_TABS[0].key);

  useEffect(() => {
    http.get("/impact/global").then((r) => setImpact(r.data)).catch(() => {});
    http.get("/opportunities").then((r) => setOpps((r.data || []).slice(0, 8))).catch(() => {});
  }, []);

  // Refetch orgs when the country filter changes; then filter by
  // cause client-side (avoids many round-trips + keeps the tab UX snappy).
  useEffect(() => {
    setOrgLoad(true);
    const params = new URLSearchParams();
    if (country) params.set("country", country);
    params.set("limit", "500");
    http.get(`/organizations?${params.toString()}`)
      .then((r) => setOrgs(Array.isArray(r.data) ? r.data : r.data?.organizations || []))
      .catch(() => setOrgs([]))
      .finally(() => setOrgLoad(false));
  }, [country]);

  const filteredOrgs = useMemo(() => {
    const active = CAUSE_TABS.find((t) => t.key === cause);
    if (!active) return orgs.slice(0, 24);
    const matchers = active.causeMatch.map((s) => s.toLowerCase());
    return orgs.filter((o) => {
      const causes = (o.causes || []).map((c) => String(c).toLowerCase());
      const cat    = String(o.category || "").toLowerCase();
      const name   = String(o.name || o.tagline || "").toLowerCase();
      return matchers.some((m) => causes.includes(m) || cat.includes(m) || name.includes(m));
    }).slice(0, 24);
  }, [orgs, cause]);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8" data-testid="volunteer-home">
      {/* ─── Hero ─── */}
      <section className="relative overflow-hidden rounded-3xl border p-6 sm:p-12"
               style={{
                 borderColor: "var(--wofy-border)",
                 background: "linear-gradient(135deg,#FEF3C7 0%,#DBEAFE 50%,#DCFCE7 100%)",
               }}
               data-testid="volunteer-hero">
        <HandHelping className="absolute -top-6 -right-6 w-40 h-40 opacity-25 text-emerald-500 rotate-12 pointer-events-none" />
        <TreePine    className="absolute -bottom-6 -left-6 w-40 h-40 opacity-25 text-emerald-700 pointer-events-none" />
        <Sparkles    className="absolute top-6 left-6 w-10 h-10 opacity-30 text-amber-500 hidden md:block pointer-events-none" />

        <div className="relative z-10 max-w-3xl">
          <div className="overline mb-1" style={{ color: "#065f46" }}>
            ❤️ VOLUNTEERS · SOCIAL CAPITAL IN ACTION
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight"
              style={{ color: "#0f172a" }}>
            Together we build <span style={{ color: "#059669" }}>stronger communities</span>.
          </h1>
          <p className="mt-4 text-lg opacity-90 leading-relaxed" style={{ color: "#1f2937" }}>
            Every hour volunteered strengthens our community. Every helping
            hand creates hope. Every connection builds Social Capital.
            Join thousands of people making a difference in neighborhoods
            around the world.
          </p>
          <p className="mt-3 italic" style={{ color: "#065f46" }}>
            Volunteer today. Create lasting change tomorrow.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/opportunities" className="wofy-btn-primary" data-testid="volunteer-cta-browse">
              <Calendar className="w-4 h-4" /> Find opportunities
            </Link>
            <Link to="/organizations" className="wofy-btn-ghost" data-testid="volunteer-cta-orgs">
              <Building2 className="w-4 h-4" /> Explore organizations
            </Link>
            <Link to="/submit/cause" className="wofy-btn-ghost text-sm" data-testid="volunteer-cta-publish">
              Publish a request →
            </Link>
            <a href="#volunteer-signup"
               className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium text-white transition-transform hover:-translate-y-0.5 shadow-md"
               style={{ background: "linear-gradient(135deg,#1d4ed8 0%,#2563eb 55%,#3b82f6 100%)" }}
               data-testid="volunteer-cta-signup-hero">
              <Sparkles className="w-4 h-4" /> Sign up to volunteer
            </a>
          </div>
        </div>
      </section>

      {/* ─── Social Capital Benefits ─── */}
      <section className="mt-12" data-testid="volunteer-benefits">
        <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>WHY VOLUNTEER</div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold" style={{ color: "#0f172a" }}>
          Nine ways giving your time gives back
        </h2>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {BENEFITS.map((b) => {
            return (
              <Link key={b.title}
                   to={`/volunteer/benefits/${b.slug}`}
                   className="group rounded-2xl border p-4 bg-white hover:shadow-md hover:-translate-y-0.5 transition block"
                   style={{ borderColor: "var(--wofy-border)" }}
                   data-testid={`volunteer-benefit-${b.slug}`}>
                <div className="flex items-center gap-3">
                  <CardIcon emoji={b.emoji} color={b.color} size="md" />
                  <div className="font-medium">{b.title}</div>
                </div>
                <p className="mt-2 text-sm opacity-80 leading-relaxed">{b.blurb}</p>
                <div className="mt-3 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider"
                     style={{ color: b.color }}>
                  Read article
                  <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ─── Benefit Quiz ─── */}
      <BenefitQuiz />

      {/* ─── Community Impact Dashboard ─── */}
      <section className="mt-12" data-testid="volunteer-impact">
        <div className="rounded-3xl overflow-hidden border p-6 sm:p-8"
             style={{
               borderColor: "var(--wofy-border)",
               background: "linear-gradient(135deg,#0f172a 0%,#1e293b 100%)",
               color: "white",
             }}>
          <div className="overline mb-1" style={{ color: "#93c5fd" }}>
            🌍 COMMUNITY IMPACT DASHBOARD
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold">
            Real people. Real change. Right now.
          </h2>
          {!impact ? (
            <div className="mt-6 inline-flex items-center gap-2 opacity-70">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading live numbers…
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
              <ImpactStat label="Volunteers"        value={impact.volunteers}         accent="#60a5fa" />
              <ImpactStat label="Organizations"     value={impact.organizations}      accent="#34d399" />
              <ImpactStat label="Volunteer sessions"value={impact.volunteer_sessions} accent="#fbbf24" />
              <ImpactStat label="Hours served"      value={impact.hours_served}       accent="#f87171" />
              <ImpactStat label="Countries"         value={impact.countries}          accent="#a78bfa" />
              <ImpactStat label="Families housed"   value={impact.families_housed}    accent="#fb7185" />
            </div>
          )}
          <div className="mt-6 opacity-70 text-sm">
            Live from the community — refreshed on every load.
          </div>
        </div>
      </section>

      {/* ─── Volunteer Requests strip ─── */}
      <section className="mt-12" data-testid="volunteer-opps">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div>
            <div className="overline" style={{ color: "var(--wofy-primary)" }}>OPEN VOLUNTEER REQUESTS</div>
            <h2 className="font-display text-2xl sm:text-3xl">Places that need you this week</h2>
          </div>
          <Link to="/opportunities" className="text-sm hover:underline"
                style={{ color: "var(--wofy-primary)" }}
                data-testid="volunteer-opps-all">
            View all opportunities →
          </Link>
        </div>
        {opps.length === 0 ? (
          <div className="wofy-card !p-6 opacity-70">
            No open opportunities right now — check back soon, or{" "}
            <Link to="/submit/cause" className="underline">publish one</Link>.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {opps.slice(0, 8).map((op) => (
              <Link key={op.id} to="/opportunities"
                    className="rounded-2xl border p-4 hover:shadow-md transition block"
                    style={{ borderColor: "var(--wofy-border)" }}
                    data-testid={`volunteer-opp-${op.id}`}>
                <div className="text-[10px] font-mono uppercase tracking-wider opacity-60">
                  {op.category || "opportunity"}
                </div>
                <div className="font-medium mt-1 leading-tight line-clamp-2">{op.title}</div>
                <p className="mt-2 text-xs opacity-80 line-clamp-3">{op.description}</p>
                <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-[11px] opacity-70">
                  {op.date && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{op.date}</span>}
                  {(op.hours != null) && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{op.hours}h</span>}
                  {op.city && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{op.city}</span>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* ─── Charities & Organizations (tabbed by cause + country filter) ─── */}
      <section className="mt-12" data-testid="volunteer-orgs">
        <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>
          CHARITIES · NONPROFITS · NGOs
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold" style={{ color: "#0f172a" }}>
          Organizations doing the work
        </h2>
        <p className="mt-2 opacity-80 max-w-2xl">
          Pick a cause to explore the organizations serving that need — then
          filter by country to find one close to you. Every profile has an
          editorial write-up, contact links, and a community message board.
        </p>

        {/* Country filter */}
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <label className="text-xs opacity-70 inline-flex items-center gap-1">
            <MapPin className="w-3 h-3" /> Country
          </label>
          <select value={country} onChange={(e) => setCountry(e.target.value)}
                  className="px-3 py-1.5 rounded-full border text-sm bg-white"
                  style={{ borderColor: "var(--wofy-border)" }}
                  data-testid="volunteer-orgs-country">
            <option value="">All countries</option>
            {COUNTRIES.map((c) => <option key={c.code} value={c.name}>{c.name}</option>)}
          </select>
        </div>

        {/* Cause tabs */}
        <div className="mt-4 flex flex-wrap gap-1.5" data-testid="volunteer-orgs-tabs">
          {CAUSE_TABS.map((t) => (
            <button key={t.key} type="button" onClick={() => setCause(t.key)}
                    data-testid={`volunteer-orgs-tab-${t.key}`}
                    className={`text-xs px-3 py-1.5 rounded-full border ${cause === t.key ? "bg-black text-white" : "hover:bg-black/5"}`}
                    style={cause === t.key ? undefined : { borderColor: "var(--wofy-border)" }}>
              <span className="mr-1">{t.icon}</span>{t.label}
            </button>
          ))}
        </div>

        {/* Org grid */}
        {orgLoad ? (
          <div className="mt-4 inline-flex items-center gap-2 opacity-60">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading organizations…
          </div>
        ) : filteredOrgs.length === 0 ? (
          <div className="mt-4 wofy-card !p-6 opacity-80" data-testid="volunteer-orgs-empty">
            <div className="font-medium mb-1">No matches yet.</div>
            <div className="text-sm opacity-80">
              Try a different cause or country. Or{" "}
              <Link to="/register/organization" className="underline">
                register your organization
              </Link>.
            </div>
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
               data-testid="volunteer-orgs-grid">
            {filteredOrgs.map((o) => <OrgCard key={o.id || o.slug} org={o} />)}
          </div>
        )}
      </section>

      {/* ─── Closing CTA ─── */}
      <section id="volunteer-signup"
               className="mt-14 rounded-3xl border p-6 sm:p-10 text-center scroll-mt-24"
               style={{
                 borderColor: "var(--wofy-border)",
                 background: "linear-gradient(135deg,#F0FDF4 0%,#EFF6FF 100%)",
               }}
               data-testid="volunteer-cta-close">
        <div className="overline mb-1" style={{ color: "#065f46" }}>READY TO BEGIN?</div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold" style={{ color: "#0f172a" }}>
          Your first hour changes everything.
        </h2>
        <p className="mt-3 max-w-xl mx-auto opacity-80">
          Sign up in under a minute, browse local opportunities, and turn
          your time into measurable Social Capital.
        </p>
        <div className="mt-5 flex flex-wrap gap-3 justify-center">
          <Link to="/register" className="wofy-btn-primary" data-testid="volunteer-cta-signup">
            <Sparkles className="w-4 h-4" /> Sign up to volunteer
          </Link>
          <Link to="/social-capital" className="wofy-btn-ghost text-sm" data-testid="volunteer-cta-learn">
            How Social Capital works <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </section>
    </div>
  );
}

/* ─── Sub-components ────────────────────────────────────────────── */
function ImpactStat({ label, value, accent }) {
  return (
    <div className="rounded-2xl p-3 border"
         style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.04)" }}
         data-testid={`volunteer-impact-${label.toLowerCase().replace(/\s+/g,"-")}`}>
      <CountUp value={value} duration={1400}
               className="block font-display text-2xl font-bold"
               style={{ color: accent }} />
      <div className="mt-1 text-[11px] uppercase font-mono tracking-wider opacity-70">
        {label}
      </div>
    </div>
  );
}

function OrgCard({ org }) {
  const monogram = (org.acronym || org.name || "?")
    .replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase() || "?";
  const logo = org.logo_url || org.image_url;
  const link = `/organizations/${org.slug || org.id}`;
  return (
    <Link to={link}
          className="rounded-2xl border p-4 hover:shadow-md transition flex gap-3"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid={`volunteer-org-${org.slug || org.id}`}>
      {logo ? (
        <div className="w-14 h-14 rounded-xl border overflow-hidden flex items-center justify-center flex-shrink-0"
             style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}>
          <img src={logo} alt={org.name} className="max-w-full max-h-full object-contain p-1" />
        </div>
      ) : (
        <div className="w-14 h-14 rounded-xl grid place-items-center flex-shrink-0 font-mono font-bold"
             style={{ background: "var(--wofy-surface)", color: "var(--wofy-primary)" }}>
          {monogram}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <div className="font-medium leading-tight truncate">{org.name}</div>
          {org.verification_tier && (
            <Shield className="w-3.5 h-3.5" style={{ color: "var(--wofy-primary)" }} title="Verified" />
          )}
        </div>
        {org.tagline && (
          <div className="text-xs opacity-70 mt-0.5 line-clamp-2">{org.tagline}</div>
        )}
        <div className="mt-1 text-[11px] font-mono opacity-60">
          {[org.hq_city, org.hq_country || org.country].filter(Boolean).join(", ") || "Global"}
        </div>
      </div>
    </Link>
  );
}
